/**
 * One `Y.Doc` and one socket per document, however many components are looking
 * at it.
 *
 * The component contract hands `DocumentEditor` and `PresenceBar` the same
 * `nodeId` and nothing else, which means each of them could construct its own
 * `HocuspocusProvider` — and then one open document would be two connections,
 * two awareness clients, and a presence bar showing the reader themselves as a
 * second person. So the provider lives in a module-level registry keyed by
 * document name, and the components take references to it.
 *
 * ## Who may open a connection
 *
 * `apps/collab/src/authenticate.ts` authorizes the socket against
 * `document.edit`, not `document.view`: a Yjs connection is a write channel, and
 * a reader in 1a renders the published version over REST. So **only the editor
 * creates a session**, and only when it is editable — `PresenceBar` joins one if
 * it exists and never starts one. That is not an optimisation. A reader who
 * opened a provider would be refused, and the provider's websocket retries with
 * backoff and no attempt limit, so the refusal would become a permanent
 * reconnect loop against a server that is correctly saying no.
 *
 * ## Why the state is a snapshot object
 *
 * `useSyncExternalStore` compares snapshots by identity, so every field a
 * component renders — status, sync state, peers — lives in one frozen object
 * that is replaced, never mutated. A getter that built a fresh object per call
 * would re-render forever.
 */

import { HocuspocusProvider, WebSocketStatus } from '@hocuspocus/provider';
import { useEffect, useSyncExternalStore } from 'react';
import * as Y from 'yjs';
import { type CollabDenialReason, collabConnection, collabDocumentName } from '../api/collab.ts';
import { type AwarenessState, type PresenceFace, rosterFromAwareness } from './identity.ts';

/**
 * `idle` means nothing on this page has opened a connection for this document —
 * which is the normal state of a read-only view, not a failure.
 */
export type CollabStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'denied';

export interface CollabSnapshot {
  readonly doc: Y.Doc | null;
  readonly provider: HocuspocusProvider | null;
  readonly status: CollabStatus;
  /** The first handshake has completed and this client holds the server's state. */
  readonly synced: boolean;
  /** Edits made here that the server has not acknowledged. */
  readonly unsynced: number;
  /** Set when `onAuthenticate` refused the socket; see `api/collab.ts`. */
  readonly denial: CollabDenialReason | null;
  readonly self: PresenceFace | null;
  readonly others: readonly PresenceFace[];
}

const IDLE: CollabSnapshot = Object.freeze({
  doc: null,
  provider: null,
  status: 'idle',
  synced: false,
  unsynced: 0,
  denial: null,
  self: null,
  others: [] as readonly PresenceFace[],
});

interface Session {
  readonly name: string;
  readonly doc: Y.Doc;
  readonly provider: HocuspocusProvider;
  refs: number;
  snapshot: CollabSnapshot;
  sweep: ReturnType<typeof setTimeout> | null;
}

const sessions = new Map<string, Session>();

/**
 * Listeners outlive sessions. `PresenceBar` subscribes before any editor has
 * mounted and has to be told when one appears, so the subscription cannot hang
 * off the session object.
 */
const listeners = new Map<string, Set<() => void>>();

/**
 * How long a session survives its last reference.
 *
 * React's strict mode mounts, unmounts, and remounts every effect, so a
 * reference count that tore down at zero would destroy the socket and the
 * `Y.Doc` between the two mounts of a single editor — a full reconnect and
 * resync on every render pass in development, and a dropped session on any
 * remount in production.
 */
const TEARDOWN_GRACE_MS = 2_000;

function notify(name: string): void {
  for (const listener of listeners.get(name) ?? []) listener();
}

function update(session: Session, patch: Partial<CollabSnapshot>): void {
  session.snapshot = Object.freeze({ ...session.snapshot, ...patch });
  notify(session.name);
}

function statusOf(status: WebSocketStatus): CollabStatus {
  if (status === WebSocketStatus.Connected) return 'connected';
  if (status === WebSocketStatus.Connecting) return 'connecting';
  return 'disconnected';
}

function readRoster(session: Session, states: readonly AwarenessState[]): void {
  // `doc.clientID` rather than `provider.awareness.clientID`: they are the same
  // number — `Awareness` takes its id from the document it was built for — and
  // reading it off the document means this works before the provider finishes
  // constructing.
  const roster = rosterFromAwareness(states, session.doc.clientID);
  update(session, { self: roster.self, others: roster.others });
}

function create(nodeId: string): Session {
  const doc = new Y.Doc();
  const connection = collabConnection(nodeId);

  // `HocuspocusProvider` connects inside its own constructor, so a handler can
  // fire before there is a session object to record it against. Only the
  // constructor's own `connecting` status can land in that window, and the
  // initial snapshot below already says `connecting`; everything else needs a
  // round trip, which cannot complete synchronously.
  let session: Session | undefined;

  const provider = new HocuspocusProvider({
    ...connection,
    document: doc,
    onStatus: ({ status }) => {
      // A denial is terminal until the page reloads, so it must not be painted
      // over by the `disconnected` that follows the server closing the socket.
      if (!session || session.snapshot.denial) return;
      update(session, { status: statusOf(status) });
    },
    onSynced: ({ state }) => session && update(session, { synced: state }),
    onDisconnect: () => session && update(session, { synced: false }),
    onUnsyncedChanges: ({ number }) => session && update(session, { unsynced: number }),
    onAuthenticated: () => session && update(session, { denial: null }),
    onAuthenticationFailed: ({ reason }) => {
      if (!session) return;
      update(session, { status: 'denied', denial: reason as CollabDenialReason, synced: false });
      // Stop retrying. The provider's websocket reconnects with backoff and no
      // attempt limit, and `onAuthenticate` is deterministic — a refusal will be
      // a refusal on the next attempt too, so retrying only turns a correct
      // denial into sustained load and a log full of the same line.
      session.provider.disconnect();
    },
    onAwarenessChange: ({ states }) => session && readRoster(session, states as AwarenessState[]),
  });

  session = {
    name: connection.name,
    doc,
    provider,
    refs: 0,
    snapshot: Object.freeze({ ...IDLE, doc, provider, status: 'connecting' as CollabStatus }),
    sweep: null,
  };

  sessions.set(session.name, session);
  // A session created during a render that React then discards would otherwise
  // hold a socket open with nothing referencing it.
  scheduleSweep(session);
  return session;
}

function scheduleSweep(session: Session): void {
  if (session.sweep) clearTimeout(session.sweep);
  session.sweep = setTimeout(() => {
    session.sweep = null;
    if (session.refs > 0) return;
    sessions.delete(session.name);
    session.provider.destroy();
    session.doc.destroy();
    notify(session.name);
  }, TEARDOWN_GRACE_MS);
}

/**
 * The session for a document, opening one if there is none.
 *
 * Called during render by the editor, which needs the `Y.Doc` to build its
 * extension list in the same pass. That makes it a side effect in render, and
 * the deferred sweep above is what makes it safe: a session nothing goes on to
 * reference is collected a moment later.
 */
export function ensureSession(nodeId: string): Session {
  return sessions.get(collabDocumentName(nodeId)) ?? create(nodeId);
}

function retain(nodeId: string): Session {
  const session = ensureSession(nodeId);
  session.refs += 1;
  if (session.sweep) {
    clearTimeout(session.sweep);
    session.sweep = null;
  }
  return session;
}

/**
 * Takes the session object, not the node id.
 *
 * A component releases the session it retained, which may no longer be the one
 * registered under that name — a sweep can land between the two and the next
 * `ensureSession` builds a fresh one. Decrementing by name would then take a
 * reference off a session somebody else is holding, drive its count to zero
 * under a live consumer, and tear down a socket that is in use.
 */
function release(session: Session): void {
  session.refs -= 1;
  if (session.refs <= 0 && sessions.get(session.name) === session) scheduleSweep(session);
}

function subscribe(nodeId: string, listener: () => void): () => void {
  const name = collabDocumentName(nodeId);
  const set = listeners.get(name) ?? new Set<() => void>();
  set.add(listener);
  listeners.set(name, set);
  return () => {
    set.delete(listener);
    if (set.size === 0) listeners.delete(name);
  };
}

function snapshotOf(nodeId: string): CollabSnapshot {
  return sessions.get(collabDocumentName(nodeId))?.snapshot ?? IDLE;
}

export interface UseCollabSessionOptions {
  /**
   * Whether this component may *open* a connection. False still subscribes to
   * one somebody else opened — which is how `PresenceBar` shows who is editing
   * without a reader ever attempting a socket they would be refused.
   */
  connect: boolean;
  /** Published on the awareness channel once known. */
  identity?: PresenceFace | null;
}

/**
 * Subscribes to a document's collaboration session.
 *
 * Returns {@link IDLE} when there is none, which every caller can render: "no
 * session" and "session that has not connected yet" are different states and
 * only the second is worth a spinner.
 */
export function useCollabSession(nodeId: string, options: UseCollabSessionOptions): CollabSnapshot {
  const { connect, identity } = options;
  if (connect) ensureSession(nodeId);

  const snapshot = useSyncExternalStore(
    (listener) => subscribe(nodeId, listener),
    () => snapshotOf(nodeId),
  );

  useEffect(() => {
    if (!connect) return;
    const held = retain(nodeId);
    return () => release(held);
  }, [nodeId, connect]);

  const provider = snapshot.provider;
  const personId = identity?.personId ?? null;
  const name = identity?.name ?? null;
  const color = identity?.color ?? null;

  useEffect(() => {
    if (!provider || !personId || !name || !color) return;
    // Set here as well as on `CollaborationCaret` because the two learn the
    // person at different moments: the caret is configured when the editor is
    // built, and `GET /auth/me` may not have resolved by then. Both write the
    // same key with the same value, so the later write is a no-op rather than a
    // second identity.
    provider.setAwarenessField('user', { personId, name, color });
  }, [provider, personId, name, color]);

  return snapshot;
}
